import {
  resolveWakeLoopEnvPreset,
  resolveWakeLoopRootDir,
  type WakeLoopEnvPreset,
} from "./paths";

export type WakeLoopLaneDescriptor = {
  command: "wakeloop" | "wakeloop-dev" | "wakeloop-canary";
  preset: WakeLoopEnvPreset;
  wakeLoopHome: string;
  destructiveResetAllowed: boolean;
  repairCommand: string;
  removeCommand: string | null;
  resetCommand: string;
};

export function resolveWakeLoopLaneDescriptor(input?: {
  env?: NodeJS.ProcessEnv;
  argv?: string[];
  homeDir?: string;
}): WakeLoopLaneDescriptor {
  const env = input?.env ?? process.env;
  const preset = resolveWakeLoopEnvPreset({
    argv: input?.argv,
    env,
    homeDir: input?.homeDir,
  });
  const command = resolveLaneCommand(preset);
  return {
    command,
    preset,
    wakeLoopHome: resolveWakeLoopRootDir({
      argv: input?.argv,
      env,
      homeDir: input?.homeDir,
    }),
    destructiveResetAllowed: true,
    repairCommand: `${command} repair local-state`,
    removeCommand: preset === "canary" ? "pnpm wakeloop:canary:remove" : null,
    resetCommand: `${command} reset --yes`,
  };
}

export function formatLaneScopeSentence(
  lane: Pick<WakeLoopLaneDescriptor, "wakeLoopHome" | "command">
): string {
  return `This only affects the ${lane.command} lane at ${lane.wakeLoopHome}.`;
}

export function buildLaneScopedCleanupGuidance(
  lane: Pick<
    WakeLoopLaneDescriptor,
    "command" | "repairCommand" | "removeCommand"
  >
): string {
  const commands = [`\`${lane.repairCommand}\``];
  if (lane.removeCommand) {
    commands.unshift(`\`${lane.removeCommand}\``);
  }
  return `Optional cleanup for this lane: run ${commands.join(" or ")}.`;
}

function resolveLaneCommand(
  preset: WakeLoopEnvPreset
): WakeLoopLaneDescriptor["command"] {
  if (preset === "dev-local") {
    return "wakeloop-dev";
  }
  if (preset === "canary") {
    return "wakeloop-canary";
  }
  return "wakeloop";
}
